/**
 * Transform type guard function into an assertion function
 */
export function assertType<T, U extends T>(
  typeGuard: (t: T) => t is U,
): (x: T) => asserts x is U;
export function assertType<T, U extends T>(
  typeGuard: (t: T) => t is U,
  x: T,
): asserts x is U;
export function assertType<T, U extends T>(
  ...args: [typeGuard: (t: T) => t is U, x?: T]
): unknown {
  const [typeGuard, x] = args;
  switch (args.length) {
    case 2:
      if (!typeGuard(x!)) {
        throw new TypeError('Type mismatch');
      }
      return x;
    case 1:
      return (x0: T) => assertType(typeGuard, x0);
    default:
      throw new Error('Invalid argument: assertType expects 1 or 2 arguments');
  }
}

/**
 * Like assertType, but passes through the tested variable on success, which is more useful
 * in some scenarios like chaining functions
 */
export const narrowType: {
  <T, U extends T>(typeGuard: (t: T) => t is U): (x: T) => U;
  <T, U extends T>(typeGuard: (t: T) => t is U, x: T): U;
} = assertType;
